import { Box, CircularProgress, styled, Typography } from "@mui/material";
import SparklessIcon from "../../../../../Assets/Icons/Sparkless.svg";
import ImageUpload from "../../../../../Components/ImageUpload";
import useHomeStore from "../../../../../stores/useHomeStore";
import {
  AIWeddingImageEditPlaceholder,
  AIWeddingPlannerChips,
  AIWeddingPlannerPlaceholder,
  AIWeddingPlannerPlaceholderContinuePlanning,
  AIWeddingPlannerPlaceholderResumeSession,
  MAX_WORDS,
  placeholderHomeTextArea,
  SelectedMode,
} from "../constant";
import {
  ActionButton,
  ButtonsContainer,
  CharacterCounter,
  Chip,
  Chips,
  CopyIconContainer,
  DisabledButtonWrapper,
  HeaderTextArea,
  LanguageCapsuleContainer,
  LanguageText,
  StyledTextField,
  TextAreaContainer,
  TextAreaFooter,
} from "../styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AnimatedPlaceholder from "../../../../../Components/AnimatedPlaceholder";
import useGetChatList from "../../../../../Hooks/useGetChatList";
import useGetChatDetail from "../../../../../Hooks/useGetChatDetail";
import CopyIcon from "../../../../../Components/CopyIcon";
import { useNavigate } from "react-router-dom";
import useUserCreditData from "../../../../../Hooks/useUserCreditData";
import PromptWizard from "../../../../../Components/PromptWizard";
import FailModal from "../../../../../Components/FailModal";
import {
  FluxModel,
  ImageAspectRatioOptions,
  ImageAspectRatioOptionsMap,
  MB,
} from "../../ADS/constant";
import usePostProduct from "../../../../../Hooks/usePostProduct";
import useGetProductStatus from "../../../../../Hooks/useGetProductStatus";
import { countWords } from "../utils";
import ADSChip from "../../ADS/Component/ADSContent/Component/ADSContentLeft/Component/ADSChip";
import OutOfCreditsModal from "../../../../../Components/OutOfCreditsModal";

const PanelContainer = styled(Box)({
  flex: 3,
});

const MainPanel = () => {
  const navigate = useNavigate();
  const {
    selectedMode,
    originalPrompt,
    previousCreateImagePrompt,
    previousPlanYourWeddingPrompt,
    previousAIImageEditPrompt,

    setOriginalPrompt,
    setPreviousCreateImagePrompt,
    setPreviousPlanYourWeddingPrompt,
    setPreviousAIImageEditPrompt,
  } = useHomeStore();

  const { userCredits, isLoading: isLoadingUserCredit } = useUserCreditData();
  const postProduct = usePostProduct();
  const getProductStatus = useGetProductStatus();

  const textAreaId = "home-text-area";
  const textAreaRef = useRef(null);
  const isAIWeddingPlanner = selectedMode === SelectedMode.PlanYourWedding;
  const isAIWeddingImageEdit = selectedMode === SelectedMode.AIImageEdit;
  const isAIWeddingVisualizer = selectedMode === SelectedMode.CreateImage;

  const { data: chatList } = useGetChatList();
  const { data: initialChatData } = useGetChatDetail({
    id: chatList?.[chatList.length - 1]?.id,
  });

  const chatId = useMemo(() => sessionStorage.getItem("chatId"), []);
  const hasChatSession = useMemo(() => !!chatId, [chatId]);
  const hasChatHistory = useMemo(
    () => initialChatData.length > 0,
    [initialChatData],
  );

  const [isInputFocused, setInputFocused] = useState(false);
  const [placeHolderLanguage, setPlaceHolderLanguage] = useState(
    placeholderHomeTextArea[0].language,
  );
  const [imageInputFile, setImageInputFile] = useState(null);
  const [aspectRatio, setAspectRatio] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [errorModal, setErrorModal] = useState({
    show: false,
    title: "",
    description: "",
    cancelText: "Close",
  });
  const [outOfCreditsModal, setOutOfCreditsModal] = useState({
    show: false,
    variant: "",
  });

  const promptText = useMemo(() => {
    switch (selectedMode) {
      case SelectedMode.CreateImage:
        return previousCreateImagePrompt;
      case SelectedMode.PlanYourWedding:
        return previousPlanYourWeddingPrompt;
      case SelectedMode.AIImageEdit:
        return previousAIImageEditPrompt;
      default:
        return "";
    }
  }, [
    previousAIImageEditPrompt,
    previousCreateImagePrompt,
    previousPlanYourWeddingPrompt,
    selectedMode,
  ]);

  const emptyPromptText = useCallback(() => {
    setPreviousCreateImagePrompt("");
    setPreviousPlanYourWeddingPrompt("");
    setPreviousAIImageEditPrompt("");
  }, [
    setPreviousAIImageEditPrompt,
    setPreviousCreateImagePrompt,
    setPreviousPlanYourWeddingPrompt,
  ]);

  const setPromptText = useCallback(
    (promptText) => {
      switch (selectedMode) {
        case SelectedMode.CreateImage:
          setPreviousCreateImagePrompt(promptText);
          break;
        case SelectedMode.PlanYourWedding:
          setPreviousPlanYourWeddingPrompt(promptText);
          break;
        case SelectedMode.AIImageEdit:
          setPreviousAIImageEditPrompt(promptText);
          break;
        default:
          break;
      }
    },
    [
      selectedMode,
      setPreviousAIImageEditPrompt,
      setPreviousCreateImagePrompt,
      setPreviousPlanYourWeddingPrompt,
    ],
  );

  const showPlaceholder = !isInputFocused && promptText === "";
  const hasLessThan1Credits = userCredits.available < 1; // default model FluxModel.dev requires 3 credit
  const hasLessThan5Credits = userCredits.available < 5; // flux kontext requires 5 credit

  const disabledPromptWizard = countWords(promptText) <= 1;

  const showImageAspectRatioOptions =
    isAIWeddingImageEdit || isAIWeddingVisualizer;

  const closeModal = () => {
    setErrorModal({
      show: false,
      title: "",
      description: "",
      cancelText: "Close",
    });
  };

  const closeOutOfCreditsModal = () => {
    setOutOfCreditsModal({
      show: false,
      variant: "",
    });
  };

  const handleRetry = useCallback(() => {
    navigate("/Home/Profile?selectedTab=credit");
  }, [navigate]);

  const handleStartChatbot = (promptText) => {
    if (promptText) {
      sessionStorage.setItem("promptText", promptText);
    }

    if (!chatId && chatList.length > 0) {
      sessionStorage.setItem("chatId", chatList[chatList.length - 1].id);
    }

    navigate("/Home/Ai-Agent");
    setOriginalPrompt("");
    emptyPromptText();
    return;
  };

  const showInappropriatePromptModal = () => {
    setErrorModal((prev) => ({
      ...prev,
      show: true,
      title: "Inappropriate Content Detected",
      description:
        "Your prompt may contain inappropriate content (e.g., sexual references or hate speech), or it may have been misinterpreted by our AI. Please revise it and try again to help us maintain a respectful environment. Thank you.",
      cancelText: "Close",
    }));
  };

  const showGeneralErrorModal = () => {
    setErrorModal((prev) => ({
      ...prev,
      show: true,
      title: "Something Went Wrong",
      description:
        "We apologize, but it looks like there was an error during generation. Your credit has not been deducted. Please try again.",
      cancelText: "Close",
    }));
  };

  const handleStartGenerateFluxKontext = () => {
    setGeneratingImage(true);
    postProduct({
      projectId: -1,
      form: {
        model: FluxModel.FluxKontext,
        prompt: promptText,
        imageInputFile: imageInputFile,
        aspectRatio: aspectRatio,
      },
      isExternalModel: true,
      supportsAspectRatio: true,
    })
      .then((res) =>
        getProductStatus({ productId: res.id }).then((statusRes) => {
          console.log(statusRes);

          if (statusRes?.includes("Inappropriate image has been generated")) {
            showInappropriatePromptModal();
            return;
          }

          const projectId = res?.project?.id;
          if (projectId) {
            navigate("/Home/AI_Studio/" + projectId);
            setOriginalPrompt("");
            emptyPromptText();
          }
        }),
      )
      .catch((err) => {
        console.error(err);
        if (err?.response?.data?.message === "error.inappropriateprompt") {
          showInappropriatePromptModal();
        } else if (err?.response?.data?.message === "error.http.413") {
          setErrorModal({
            show: true,
            retry: false,
            title: "Image Too Large",
            description:
              "The image you tried to upload is too large. The maximum file size allowed is 10 MB. Please upload a smaller image and try again. Thank You!",
          });
        } else {
          showGeneralErrorModal();
        }
      })
      .finally(() => setGeneratingImage(false));
  };

  const handleTextChange = (e) => {
    const value = e.target.value;
    const inputType = e.nativeEvent.inputType;
    const isPaste = inputType === "insertFromPaste";

    // Disable character count for AI Wedding Planner
    if (isAIWeddingPlanner) {
      setPromptText(value);
    }

    if (countWords(promptText) >= MAX_WORDS) {
      // Only allow changes that don't add new spaces
      if (value.length > promptText.length && /\s/.test(value.slice(-1))) {
        return;
      }
    }

    const wordCount = countWords(value);
    if (isPaste && wordCount > MAX_WORDS) {
      // Cut to 100 words
      const words = value.trim().split(/\s+/);
      const truncatedText = words.slice(0, MAX_WORDS).join(" ");
      setPromptText(truncatedText);
      setOriginalPrompt(truncatedText);
    } else {
      setPromptText(value);
      setOriginalPrompt(value);
    }
  };

  const handleStartGenerateImage = () => {
    setGeneratingImage(true);
    postProduct({
      projectId: -1,
      form: {
        model: FluxModel.Dev,
        prompt: promptText,
        aspectRatio: aspectRatio,
      },
      isExternalModel: true,
      supportsAspectRatio: true,
    })
      .then((res) =>
        getProductStatus({ productId: res.id }).then((statusRes) => {
          console.log(statusRes);

          if (statusRes?.includes("Inappropriate image has been generated")) {
            showInappropriatePromptModal();
            return;
          }

          const projectId = res?.project?.id;
          if (projectId) {
            navigate("/Home/AI_Studio/" + projectId);
          }
          setOriginalPrompt("");
          emptyPromptText();
        }),
      )
      .catch((err) => {
        console.error(err);
        if (err?.response?.data?.message === "error.inappropriateprompt") {
          showInappropriatePromptModal();
        } else {
          showGeneralErrorModal();
        }
      })
      .finally(() => setGeneratingImage(false));
  };

  const handleGenerateClick = () => {
    if (isAIWeddingPlanner) {
      handleStartChatbot(promptText);
      return;
    }

    if (isAIWeddingImageEdit) {
      if (!imageInputFile) {
        setErrorModal((prev) => ({
          ...prev,
          show: true,
          title: "Image Required for AI Editing",
          description:
            "You need to upload an image before you can continue. Once your image is uploaded, enter your prompt and let WeddinglyAi work its magic to edit your image.",
          cancelText: "Ok",
        }));
        return;
      }

      if (imageInputFile.size > 10 * MB) {
        setErrorModal({
          show: true,
          retry: false,
          title: "Image Too Large",
          description:
            "The image you tried to upload is too large. The maximum file size allowed is 10 MB. Please upload a smaller image and try again. Thank You!",
        });
        return;
      }

      if (hasLessThan5Credits) {
        setOutOfCreditsModal({
          show: true,
          variant: "lessThanRequiredCredits",
        });
        return;
      }

      handleStartGenerateFluxKontext();
      return;
    }

    if (isAIWeddingVisualizer) {
      if (hasLessThan1Credits) {
        setOutOfCreditsModal({
          show: true,
          variant: "lessThanRequiredCredits",
        });
        return;
      }

      handleStartGenerateImage();
      return;
    }
  };

  const handleSelectWizardPrompt = (prompt) => {
    setPromptText(prompt);
  };

  const handleChangeAspectRatio = (newAspectRatio) => {
    if (aspectRatio === newAspectRatio) {
      setAspectRatio(null);
      return;
    }

    setAspectRatio(newAspectRatio);
  };

  const renderActionButton = (
    label,
    icon,
    onClick,
    alwaysEnabled = false,
    halfWidth = false,
  ) => {
    const hasLessThanRequiredCredits = isAIWeddingImageEdit
      ? hasLessThan5Credits
      : hasLessThan1Credits;

    const disableGenerate =
      (isLoadingUserCredit ||
        countWords(promptText) < 3 ||
        countWords(promptText) > MAX_WORDS) &&
      !alwaysEnabled;

    const iconColorStyle = disableGenerate
      ? {
          filter: "brightness(0) saturate(100%)",
          WebkitMask: `url(${icon}) no-repeat center / contain`,
          mask: `url(${icon}) no-repeat center / contain`,
          background: "#FB4764",
          height: "20px",
          width: "20px",
          opacity: 0.1,
          display: "inline-block",
        }
      : {
          height: "20px",
          width: "20px",
        };

    const startIcon =
      generatingImage || isLoadingUserCredit ? (
        <CircularProgress sx={{ color: "#FFFFFF" }} size={24} />
      ) : disableGenerate ? (
        <span style={iconColorStyle}></span>
      ) : (
        <img src={icon} alt={label} style={iconColorStyle} />
      );

    const button = (
      <ActionButton
        variant="contained"
        onClick={onClick}
        disabled={disableGenerate}
        startIcon={startIcon}
        halfWidth={halfWidth}
      >
        {label}
      </ActionButton>
    );

    if (hasLessThanRequiredCredits && !alwaysEnabled && !isLoadingUserCredit) {
      return (
        <DisabledButtonWrapper sx={{ flex: 1 }}>{button}</DisabledButtonWrapper>
      );
    }

    return button;
  };

  const renderPlaceholder = useMemo(() => {
    if (isAIWeddingVisualizer) {
      return placeholderHomeTextArea;
    }

    if (isAIWeddingImageEdit) {
      return AIWeddingImageEditPlaceholder;
    }

    // AI Wedding Planner
    return hasChatSession
      ? AIWeddingPlannerPlaceholderResumeSession
      : chatList.length > 0
        ? AIWeddingPlannerPlaceholderContinuePlanning
        : AIWeddingPlannerPlaceholder;
  }, [isAIWeddingImageEdit, isAIWeddingVisualizer, hasChatSession, chatList]);

  useEffect(() => {
    if (
      typeof userCredits.available === "number" &&
      userCredits.available < 3 &&
      !isLoadingUserCredit
    ) {
      const isModalShown = sessionStorage.getItem(
        "homeNotEnoughCreditsModalShown",
      );

      if (!isModalShown) {
        setOutOfCreditsModal({
          show: true,
          variant: "lessThanRequiredCredits",
        });
        sessionStorage.setItem("homeNotEnoughCreditsModalShown", "true");
      }
    }
  }, [isLoadingUserCredit, userCredits]);

  return (
    <PanelContainer>
      {isAIWeddingImageEdit && (
        <ImageUpload
          onUpload={setImageInputFile}
          imageInputFile={imageInputFile}
        />
      )}

      <HeaderTextArea isAIWeddingPlanner={isAIWeddingPlanner}>
        <Typography color="#757575">Prompt</Typography>
        <LanguageCapsuleContainer opacity={showPlaceholder ? 1 : 0}>
          <LanguageText>{placeHolderLanguage}</LanguageText>
        </LanguageCapsuleContainer>
      </HeaderTextArea>

      <TextAreaContainer
        sx={{ marginBottom: showImageAspectRatioOptions ? "12px" : "24px" }}
      >
        <StyledTextField
          fullWidth
          multiline
          id={textAreaId}
          ref={textAreaRef}
          variant="outlined"
          placeholder=""
          value={promptText}
          onChange={handleTextChange}
          onFocus={() => setInputFocused(true)}
          onBlur={() => setInputFocused(false)}
        />

        <AnimatedPlaceholder
          placeholders={renderPlaceholder}
          showPlaceholder={showPlaceholder}
          interval={5000} // changed from 3000 to 5000 by Armin on July 21, 2025
          onChangeLanguage={setPlaceHolderLanguage}
        />

        {isAIWeddingPlanner && (
          <>
            <Chips>
              {AIWeddingPlannerChips.map((chip, index) => (
                <Chip key={index} onClick={() => handleStartChatbot(chip)}>
                  {chip}
                </Chip>
              ))}
            </Chips>
          </>
        )}

        {isAIWeddingImageEdit && (
          <>
            <CopyIconContainer>
              <CopyIcon Id={textAreaId} />
            </CopyIconContainer>

            <TextAreaFooter>
              <CharacterCounter>
                {countWords(promptText)} out of {MAX_WORDS} keywords
              </CharacterCounter>
            </TextAreaFooter>
          </>
        )}

        {isAIWeddingVisualizer && (
          <>
            <CopyIconContainer>
              <CopyIcon Id={textAreaId} />
            </CopyIconContainer>

            <TextAreaFooter>
              <CharacterCounter>
                {countWords(promptText)} out of {MAX_WORDS} keywords
              </CharacterCounter>
            </TextAreaFooter>
          </>
        )}
      </TextAreaContainer>

      {showImageAspectRatioOptions && (
        <div style={{ display: "flex", gap: "6px", marginBottom: "24px" }}>
          {ImageAspectRatioOptions.map((option) => {
            const isActive = option.value === aspectRatio;
            return (
              <ADSChip
                key={option.value}
                label={option.label}
                active={isActive}
                onClick={() => handleChangeAspectRatio(option.value)}
                withTooltip
                aspectRatioSettings={ImageAspectRatioOptionsMap[option.value]}
              />
            );
          })}
        </div>
      )}

      <ButtonsContainer>
        {isAIWeddingPlanner && (
          <>
            {renderActionButton(
              hasChatSession
                ? "Resume My Session"
                : hasChatHistory
                  ? "Continue Planning"
                  : "Start Planning",
              SparklessIcon,
              handleGenerateClick,
              hasChatSession || hasChatHistory,
              true,
            )}
          </>
        )}

        {isAIWeddingImageEdit && (
          <>
            <PromptWizard
              originalPrompt={originalPrompt}
              onSelectWizardPrompt={handleSelectWizardPrompt}
              disabled={disabledPromptWizard}
              outline
              square
              fullWidth
              style={{
                flex: 1,
              }}
            />

            {renderActionButton("Generate", SparklessIcon, handleGenerateClick)}
          </>
        )}

        {isAIWeddingVisualizer && (
          <>
            <PromptWizard
              originalPrompt={originalPrompt}
              onSelectWizardPrompt={handleSelectWizardPrompt}
              disabled={disabledPromptWizard}
              outline
              square
              fullWidth
              style={{
                flex: 1,
              }}
            />

            {renderActionButton("Generate", SparklessIcon, handleGenerateClick)}
          </>
        )}
      </ButtonsContainer>

      <FailModal {...errorModal} onCancel={closeModal} onRetry={handleRetry} />
      <OutOfCreditsModal
        {...outOfCreditsModal}
        onClose={closeOutOfCreditsModal}
        onSubscribe={handleRetry}
      />
    </PanelContainer>
  );
};

export default MainPanel;
